<template>
  <v-menu offset-y location="bottom right">
    <template #activator="{ props }">
      <v-btn rounded color="primary" v-bind="props" size="large" prepend-icon="$add">
        {{ $t("general.add") }}
      </v-btn>
    </template>
    <v-list>
      <v-list-item v-for="action in actions" :key="action" @click="handleAction(action)">
        <v-list-item-title>
          {{ $t(`phishingSettings.actions.${action}`) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useDialogsStore } from "@/_store/dialogs.module";
import { ModalWidth } from "@/constants/modals";
import { PhishingAction } from "@/constants/phishing.ts";
import EmailSettingsAllowBlockListModal from "@/components/modals/email-settings/EmailSettingsAllowBlockListModal.vue";
import ImportCSVModal from "@/components/modals/ImportCSVModal.vue";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { useMspEmailSecurityAllowBlockListStore } from "@/_store/msp/allow-block-lists/msp-email-security-allow-block-list.module.ts";
import type { ValidateAllowBlockTermsPayload } from "@/_store/email-security/email-settings.module";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export default defineComponent({
  setup() {
    const dialogsStore = useDialogsStore();
    const allowBlockListStore = useMspEmailSecurityAllowBlockListStore();

    const actionNotAllowed = computed(() =>
      isMspActionRestricted(
        MspRolePermissionScopes.MSP_PORTAL_SCOPE,
        MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS
      )
    );

    const handleAction = (action: PhishingAction) => {
      switch (action) {
        case PhishingAction.ADD_TO_ALLOWLIST:
        case PhishingAction.ADD_TO_BLOCKLIST:
          dialogsStore.openDialog(
            componentDialogsConfigConstructor({
              item: {
                allowList: action === PhishingAction.ADD_TO_ALLOWLIST,
                validate: (payload: ValidateAllowBlockTermsPayload) =>
                  allowBlockListStore.validateAllowBlockTerms(payload),
                fetchExistingTlds: () => allowBlockListStore.getExistingTlds(),
                fetchRestrictedTlds: () => allowBlockListStore.getRestrictedTlds(),
              },
              action: action,
              component: EmailSettingsAllowBlockListModal,
              width: ModalWidth.LARGE,
              callback: async (data) => {
                await allowBlockListStore.addToAllowBlockList(data);
              },
              disable: actionNotAllowed.value,
            })
          );
          break;
        case PhishingAction.IMPORT_ALLOW_BLOCK_LIST:
          dialogsStore.openDialog(
            componentDialogsConfigConstructor({
              item: {
                downloadTemplateCallback: allowBlockListStore.downloadAllowBlockListExampleCsv,
                uploadCallback: async (data: File) => {
                  await allowBlockListStore.importAllowBlockListFromCsv(data);
                  await allowBlockListStore.getAllowBlockList();
                },
              },
              action: PhishingAction.DOWNLOAD_ALLOW_BLOCK_LIST_EXAMPLE_CSV,
              component: ImportCSVModal,
              width: ModalWidth.LARGE,
              callback: () => {},
              hideFooter: true,
              disable: actionNotAllowed.value,
            })
          );
          break;
      }
    };

    return {
      actions: [
        PhishingAction.ADD_TO_ALLOWLIST,
        PhishingAction.ADD_TO_BLOCKLIST,
        PhishingAction.IMPORT_ALLOW_BLOCK_LIST,
      ],
      handleAction,
    };
  },
});
</script>
